import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deferAccessReview,
  fetchAccessReview,
} from "@/services/integrations/accessReviews";
import { queryKeys } from "@/query/queryKeys";
import { connectReviewedIntegration } from "./accessReviewConnection";

export function useIntegrationAccessReview(block) {
  const [connectingToolkit, setConnectingToolkit] = useState(null);
  const [error, setError] = useState(null);
  const [unusableToolkits, setUnusableToolkits] = useState({});
  const [connectedToolkits, setConnectedToolkits] = useState([]);
  const queryClient = useQueryClient();
  const { data, refetch } = useQuery({
    queryKey: queryKeys.integrations.accessReview(
      block.assistant_message_id,
      block.review_id,
    ),
    queryFn: ({ signal }) =>
      fetchAccessReview(block.assistant_message_id, block.review_id, signal),
    enabled: Boolean(block.assistant_message_id && block.review_id),
    staleTime: 15_000,
  });
  const review = useMemo(() => {
    const source = data ?? block;
    const groups = Array.isArray(source.groups) ? source.groups : [];
    return {
      ...source,
      groups: groups.map((group) => {
        const failure =
          unusableToolkits[String(group.toolkit_slug ?? "").toLowerCase()];
        return failure
          ? {
              ...group,
              connection_unusable: true,
              connection_error_detail: failure,
            }
          : group;
      }),
    };
  }, [block, data, unusableToolkits]);

  const connect = useCallback(
    async (toolkitSlug) => {
      setError(null);
      setConnectingToolkit(toolkitSlug);
      try {
        const key = String(toolkitSlug ?? "").toLowerCase();
        const result = await connectReviewedIntegration(
          block.assistant_message_id,
          block.review_id,
          toolkitSlug,
          { reconnect: Boolean(unusableToolkits[key]) },
        );
        if (result?.healthy === false) {
          setUnusableToolkits((current) => ({
            ...current,
            [key]:
              result.detail ||
              "The connected account cannot be used for this task.",
          }));
          return;
        }
        setUnusableToolkits((current) => {
          if (!current[key]) return current;
          const next = { ...current };
          delete next[key];
          return next;
        });
        await refetch();
        await queryClient.invalidateQueries({
          queryKey: ['integrations', 'connections'],
        });
        setConnectedToolkits((current) =>
          current.includes(key) ? current : [...current, key],
        );
      } catch (cause) {
        setError(
          cause instanceof Error
            ? cause.message
            : "Connection could not be started.",
        );
      } finally {
        setConnectingToolkit(null);
      }
    },
    [
      block.assistant_message_id,
      block.review_id,
      queryClient,
      refetch,
      unusableToolkits,
    ],
  );

  const defer = useCallback(async () => {
    setError(null);
    try {
      const next = await deferAccessReview(
        block.assistant_message_id,
        block.review_id,
      );
      queryClient.setQueryData(
        queryKeys.integrations.accessReview(
          block.assistant_message_id,
          block.review_id,
        ),
        next,
      );
    } catch {
      setError("Could not save this request for later.");
    }
  }, [block.assistant_message_id, block.review_id, queryClient]);

  return { review, connectingToolkit, connectedToolkits, error, connect, defer };
}
